import { isEqual, pick, startCase, uniq, uniqWith } from 'es-toolkit';
import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Divider } from '@mui/material';
import { createSelector } from 'reselect';

import { INDETERMINATE, MaterialPropertyValuePair, PropertySelectField } from '@paragon/client-tools';
import {
  formatLumberSpeciesLong,
  PlateGeometryType,
  PlateOption,
  PlatePair,
  PlateType,
} from '@paragon/contract-library';
import { PropertiesPanel, PropertiesSubpanel } from '@paragon/layout-models';
import {
  hasCanUsePlatesPermission,
  hasPricingPermission as hasPricingPermissionSelector,
  hasTrussDesignPermission as hasTrussDesignPermissionSelector,
} from '@paragon/login-tools';
import { AngleField } from '@paragon/material';
import {
  comparePlateGeometryType,
  getPlateSizeString,
  getPlateTypeString,
  isComboPlateType,
  isPlateAnalysisSupported,
  plateTypeWithTangibleTypesEqualityComparator,
} from '@paragon/material-lists';
import { notNullish, sortBy } from '@paragon/utilities';

import { plateOptionsAreEqual } from '../../../../plateOptionsAreEqual';
import { RootState } from '../../../../store';
import { changePlatePairAngle, changePlatePairs } from '../../../../store/actions/editPlatePairActions';
import {
  getActiveComponentDesign,
  getActiveComponentDesignGuid,
} from '../../../../store/selectors/getActiveComponentDesignSelector';
import { getMaxJsiForSingleSelectedItem } from '../../../../store/selectors/getMaxCsiOrJsiForSelectedItemSelector';
import {
  getSelectedItemEntries,
  getSingleSelectedItemEntryOrNull,
} from '../../../../store/selectors/getSelectedItemSelector';
import {
  getPlateTypesInInventory,
  getUniquePlatesInMaterialSetForActiveComponentDesign,
} from '../../../../store/selectors/getUniquePlatesInPlateList';
import { getPlateManufacturerFormatter, getPlateTypeFormatter } from '../../../../store/selectors/platePairFormatters';
import { getPlatePairPrice } from '../../../../store/selectors/stockLengthAndPriceSelectors';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { PropertiesPanelMenuButton } from './PropertiesPanelMenuButton';

export type PlateDimensions = Readonly<{ width: number; length: number; geometryType: PlateGeometryType }>;

export function getPlateDimensionOptions(
  inventoryPlates: readonly PlateOption[],
  ...plateTypes: readonly Pick<PlatePair, 'plateType' | 'tangiblePlateTypesForComboPlateType'>[]
) {
  const relevantPlates = uniqWith(
    plateTypes.flatMap((plateType) =>
      inventoryPlates.filter((plateOption: PlateOption) =>
        plateTypeWithTangibleTypesEqualityComparator(plateType, plateOption),
      ),
    ),
    plateOptionsAreEqual,
  );

  return sortBy(relevantPlates, [
    (plateOption) => plateOption.width,
    (plateOption) => plateOption.length,
    (plateOption) => comparePlateGeometryType(plateOption.geometryType),
  ]);
}

const getPlateTypeProperties = (state: RootState) => state.plateProperties.plateTypeProperties;

const getSelectedPlatePairs = createSelector(getSelectedItemEntries, (selectedEntries) =>
  selectedEntries.filter((entry) => entry.type === 'platePair').map((entry) => entry.item as PlatePair),
);

function getPriceForSingleSelectedPlatePair(state: RootState) {
  const placeholder = 'Not available';

  const selectedItem = getSingleSelectedItemEntryOrNull(state);
  if (selectedItem?.type !== 'platePair') return placeholder;

  const platePair = selectedItem.item as PlatePair;
  const platePairPrice = getPlatePairPrice(state.inventoryPrices, platePair);
  if (platePairPrice == null) return placeholder;

  return `$${platePairPrice.toFixed(2)}`;
}

export const getPlateMaterialForSelectedItem = createSelector(
  getSingleSelectedItemEntryOrNull,
  (state: RootState) => state.plateProperties.plateMaterialPropertySets,
  (selectedEntry, platePropertySets) =>
    selectedEntry?.type === 'platePair'
      ? (platePropertySets[
          getPlateTypeString(selectedEntry.item.plateType, selectedEntry.item.tangiblePlateTypesForComboPlateType)
        ] ?? null)
      : null,
);

function DesignValueFields() {
  const platePair = useAppSelector((state) => {
    const selectedEntry = getSingleSelectedItemEntryOrNull(state);
    return selectedEntry?.type === 'platePair' ? selectedEntry.item : null;
  });
  const plateMaterial = useAppSelector(getPlateMaterialForSelectedItem);

  const plateLateralResistanceValues = useAppSelector(
    (state) => state.plateProperties.plateTypeLateralResistanceValues,
  );

  if (!platePair) return null;

  if (!isPlateAnalysisSupported(platePair))
    return 'Design values not available; analysis of this plate is not yet supported.';

  if (!plateMaterial) return null;

  const myPlateLats = Object.values(plateLateralResistanceValues).filter(
    (plateLumberIndex) => plateLumberIndex.plateType === platePair.plateType,
  );

  return (
    <>
      <MaterialPropertyValuePair
        property="Allowable Shear At 0"
        value={`${plateMaterial.allowableShearAt0} psi/pair`}
      />
      <MaterialPropertyValuePair
        property="Allowable Shear At 30"
        value={`${plateMaterial.allowableShearAt30} psi/pair`}
      />
      <MaterialPropertyValuePair
        property="Allowable Shear At 60"
        value={`${plateMaterial.allowableShearAt60} psi/pair`}
      />
      <MaterialPropertyValuePair
        property="Allowable Shear At 90"
        value={`${plateMaterial.allowableShearAt90} psi/pair`}
      />
      <MaterialPropertyValuePair
        property="Allowable Shear At 120"
        value={`${plateMaterial.allowableShearAt120} psi/pair`}
      />
      <MaterialPropertyValuePair
        property="Allowable Shear At 150"
        value={`${plateMaterial.allowableShearAt150} psi/pair`}
      />
      <MaterialPropertyValuePair property="Shear Efficiency At 0" value={plateMaterial.shearEfficiencyAt0} />
      <MaterialPropertyValuePair property="Shear Efficiency At 30" value={plateMaterial.shearEfficiencyAt30} />
      <MaterialPropertyValuePair property="Shear Efficiency At 60" value={plateMaterial.shearEfficiencyAt60} />
      <MaterialPropertyValuePair property="Shear Efficiency At 90" value={plateMaterial.shearEfficiencyAt90} />
      <MaterialPropertyValuePair property="Shear Efficiency At 120" value={plateMaterial.shearEfficiencyAt120} />
      <MaterialPropertyValuePair property="Shear Efficiency At 150" value={plateMaterial.shearEfficiencyAt150} />
      <MaterialPropertyValuePair
        property="Maximum Tensile Efficiency at 0"
        value={plateMaterial.tensileEfficiencyAt0_MaximumNetSection}
      />
      <MaterialPropertyValuePair
        property="Maximum Tensile Efficiency at 90"
        value={plateMaterial.tensileEfficiencyAt90_MaximumNetSection}
      />
      <MaterialPropertyValuePair
        property="Minimum Tensile Efficiency at 0"
        value={plateMaterial.tensileEfficiencyAt0_MinimumNetSection}
      />
      <MaterialPropertyValuePair
        property="Minimum Tensile Efficiency at 90"
        value={plateMaterial.tensileEfficiencyAt90_MinimumNetSection}
      />
      <MaterialPropertyValuePair
        property="Tensile Ultimate Strength"
        value={`${plateMaterial.tensileUltimateStrength} psi/pair`}
      />
      <MaterialPropertyValuePair
        property="Tensile Yield Strength"
        value={`${plateMaterial.tensileYieldStrength} psi/pair`}
      />
      <MaterialPropertyValuePair property="Teeth Per Square Inch" value={plateMaterial.teethPerSquareInch} />
      <PropertiesSubpanel title="AA">
        {myPlateLats.map((lateralTest) => (
          <MaterialPropertyValuePair
            property={`${formatLumberSpeciesLong(lateralTest.species, lateralTest.tangibleSpecies)} - ${startCase(lateralTest.lumberTreatmentType)}`}
            value={lateralTest.aa}
            key={lateralTest.guid}
          />
        ))}
      </PropertiesSubpanel>
      <PropertiesSubpanel title="EA">
        {myPlateLats.map((lateralTest) => (
          <MaterialPropertyValuePair
            property={`${formatLumberSpeciesLong(lateralTest.species, lateralTest.tangibleSpecies)} - ${startCase(lateralTest.lumberTreatmentType)}`}
            value={lateralTest.ea}
            key={lateralTest.guid}
          />
        ))}
      </PropertiesSubpanel>
      <PropertiesSubpanel title="AE">
        {myPlateLats.map((lateralTest) => (
          <MaterialPropertyValuePair
            property={`${formatLumberSpeciesLong(lateralTest.species, lateralTest.tangibleSpecies)} - ${startCase(lateralTest.lumberTreatmentType)}`}
            value={lateralTest.ae}
            key={lateralTest.guid}
          />
        ))}
      </PropertiesSubpanel>
      <PropertiesSubpanel title="EE">
        {myPlateLats.map((lateralTest) => (
          <MaterialPropertyValuePair
            property={`${formatLumberSpeciesLong(lateralTest.species, lateralTest.tangibleSpecies)} - ${startCase(lateralTest.lumberTreatmentType)}`}
            value={lateralTest.ee}
            key={lateralTest.guid}
          />
        ))}
      </PropertiesSubpanel>
    </>
  );
}

type PropsWithPlatePairs = { platePairs: PlatePair[] };

export function PlateTypeSelect({ platePairs }: PropsWithPlatePairs) {
  const dispatch = useAppDispatch();
  const componentDesignGuid = useAppSelector(getActiveComponentDesignGuid)!;

  const allPlateTypes = useAppSelector(getPlateTypesInInventory);
  const plateTypeFormatter = useAppSelector(getPlateTypeFormatter);
  const inventoryPlates = useAppSelector(getUniquePlatesInMaterialSetForActiveComponentDesign);

  const trussPropertiesAreViewOnly = useAppSelector(propertiesShouldBeViewOnly);
  const canUsePlates = useAppSelector(hasCanUsePlatesPermission);
  const openContext = useAppSelector((state) => state.application.openContext);
  const allowUnknownValues =
    !useAppSelector(hasTrussDesignPermissionSelector) || openContext?.type === 'shared-component-set';

  const isViewOnly = trussPropertiesAreViewOnly || !canUsePlates;

  const plateTypes = uniqWith(
    platePairs.map((platePair) => ({
      plateType: platePair.plateType,
      tangiblePlateTypesForComboPlateType: platePair.tangiblePlateTypesForComboPlateType,
    })),
    plateTypeWithTangibleTypesEqualityComparator,
  );
  const commonPlateType = plateTypes.length === 1 ? plateTypes[0] : INDETERMINATE;

  function getBestOptionForEditedType(
    platePair: PlatePair,
    value: PlateType,
    tangiblePlateTypesForComboPlateType: readonly PlateType[],
  ): Partial<PlatePair> | undefined {
    const validPlateOptions = inventoryPlates.filter((plateOption) =>
      plateTypeWithTangibleTypesEqualityComparator(plateOption, {
        plateType: value,
        tangiblePlateTypesForComboPlateType,
      }),
    );
    const plateOptionWithSameDimension = validPlateOptions.find(
      (plateOption) => plateOption.width === platePair.width && plateOption.length === platePair.length,
    );

    if (plateOptionWithSameDimension) {
      return plateOptionWithSameDimension;
    }

    // otherwise, get closest dimension match via taxi cab distance
    const closestCandidates = sortBy(
      validPlateOptions,
      (plateOption) => Math.abs(plateOption.length - platePair.length) + Math.abs(plateOption.width - platePair.width),
    );
    return closestCandidates[0];
  }

  function onEditPlateType({
    plateType,
    tangiblePlateTypesForComboPlateType,
  }: Pick<PlatePair, 'plateType' | 'tangiblePlateTypesForComboPlateType'>) {
    dispatch(
      changePlatePairs(
        componentDesignGuid,
        platePairs.map((platePair) => ({
          ...platePair,
          ...getBestOptionForEditedType(platePair, plateType, tangiblePlateTypesForComboPlateType),
        })),
      ),
    );
  }

  return (
    <PropertySelectField<Pick<PlatePair, 'plateType' | 'tangiblePlateTypesForComboPlateType'>>
      property="Plate Type"
      value={commonPlateType}
      options={allPlateTypes}
      submit={onEditPlateType}
      getValue={({ plateType, tangiblePlateTypesForComboPlateType }) =>
        plateTypeFormatter(plateType, tangiblePlateTypesForComboPlateType)
      }
      getName={({ plateType, tangiblePlateTypesForComboPlateType }) =>
        plateTypeFormatter(plateType, tangiblePlateTypesForComboPlateType)
      }
      allowUnknownValues={allowUnknownValues}
      unknownValueMessage="not in current inventory"
      disabled={isViewOnly}
    />
  );
}

export function PlateDimensionsSelect({ platePairs }: PropsWithPlatePairs) {
  const dispatch = useAppDispatch();

  const isViewOnly = useAppSelector(propertiesShouldBeViewOnly);
  const openContext = useAppSelector((state) => state.application.openContext);
  const allowUnknownValues =
    !useAppSelector(hasTrussDesignPermissionSelector) || openContext?.type === 'shared-component-set';

  const componentDesignGuid = useAppSelector(getActiveComponentDesignGuid)!;

  const inventoryPlates = useAppSelector(getUniquePlatesInMaterialSetForActiveComponentDesign);

  const plateDimensionOptions = getPlateDimensionOptions(inventoryPlates, ...platePairs);

  const dimensions = uniqWith(
    platePairs.map((platePair) => pick(platePair, ['width', 'length', 'geometryType'])),
    isEqual,
  );
  const commonDimensions = dimensions.length === 1 ? dimensions[0] : INDETERMINATE;

  function onEditPlateDimension({ width, length, geometryType }: PlateDimensions) {
    const validOptions = inventoryPlates.filter(
      (option) => option.width === width && option.length === length && option.geometryType === geometryType,
    );

    dispatch(
      changePlatePairs(
        componentDesignGuid,
        platePairs
          .map((platePair) => {
            const matchingOption = validOptions.find((plateOption) => plateOption.plateType === platePair.plateType);
            if (matchingOption == null) return null;
            return { ...platePair, ...matchingOption };
          })
          .filter(notNullish),
      ),
    );
  }

  return (
    <PropertySelectField<PlateDimensions>
      property="Dimensions"
      value={commonDimensions}
      options={plateDimensionOptions}
      submit={onEditPlateDimension}
      getName={(plateDimensions) => getPlateSizeString(plateDimensions, true)}
      getValue={(plateDimensions) => getPlateSizeString(plateDimensions, true)}
      allowUnknownValues={allowUnknownValues}
      unknownValueMessage="not in current inventory"
      disabled={isViewOnly || plateDimensionOptions.length === 0}
    />
  );
}

export function PlateManufacturerProperty({ platePairs }: PropsWithPlatePairs) {
  const formatManufacturer = useAppSelector(getPlateManufacturerFormatter);

  const properties = useAppSelector(getPlateTypeProperties);
  const manufacturers = uniq(
    platePairs
      .map(
        ({ plateType, tangiblePlateTypesForComboPlateType }) =>
          properties[getPlateTypeString(plateType, tangiblePlateTypesForComboPlateType)],
      )
      .filter(notNullish)
      .map((properties) => properties.plateManufacturer),
  );

  const value = (() => {
    switch (manufacturers.length) {
      case 0:
        return 'Not loaded';

      case 1:
        return formatManufacturer(manufacturers[0]);

      default:
        return INDETERMINATE;
    }
  })();

  return <MaterialPropertyValuePair property="Plate Manufacturer" value={value} />;
}

export function PlateAngleInput({ platePairs }: PropsWithPlatePairs) {
  const dispatch = useAppDispatch();
  const isViewOnly = useAppSelector(propertiesShouldBeViewOnly);

  const angles = uniq(platePairs.map(({ angleZ }) => angleZ));
  const commonAngle = angles.length === 1 ? angles[0] : INDETERMINATE;

  return (
    <AngleField
      label="Plate Angle"
      canBeIndeterminate
      value={commonAngle}
      min={-360}
      max={360}
      disabled={isViewOnly}
      onSubmit={(value: number) => {
        dispatch(changePlatePairAngle(value, ...platePairs.map(({ guid }) => guid)));
      }}
    />
  );
}

export function PlateThicknessProperty({ platePairs }: PropsWithPlatePairs) {
  const properties = useAppSelector(getPlateTypeProperties);
  const thicknesses = uniq(
    platePairs
      .map(
        ({ plateType, tangiblePlateTypesForComboPlateType }) =>
          properties[getPlateTypeString(plateType, tangiblePlateTypesForComboPlateType)],
      )
      .filter(notNullish)
      .map((properties) => properties.thickness),
  );

  const value = (() => {
    switch (thicknesses.length) {
      case 0:
        return 'Not loaded';

      case 1:
        return `${thicknesses[0]} in.`;

      default:
        return INDETERMINATE;
    }
  })();

  return <MaterialPropertyValuePair property="Thickness" value={value} />;
}

export function PlatePairProperties() {
  const platePairs = useAppSelector(getSelectedPlatePairs);
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);

  const hasPricingPermission = useAppSelector(hasPricingPermissionSelector);

  // only applicable for a single selected plate pair
  const platePairPrice = useAppSelector(getPriceForSingleSelectedPlatePair);

  if (platePairs.length === 0) return null;

  const singlePlatePair = platePairs.length === 1 ? platePairs[0] : null;

  const title = 'Plate Pair Properties';
  const quantityAddendum = platePairs.length > 1 ? ` (${platePairs.length} selected)` : '';

  return (
    <PropertiesPanel
      title={title + quantityAddendum}
      titleButton={<PropertiesPanelMenuButton type="platePair" guid={platePairs[0].guid} />}
    >
      <PlateManufacturerProperty platePairs={platePairs} />
      <PlateTypeSelect platePairs={platePairs} />
      {singlePlatePair && isComboPlateType(singlePlatePair.plateType) && activeComponentDesign ? (
        <MaterialPropertyValuePair
          property="Allowed plate types for ComboPlate"
          value={getPlateTypeString(singlePlatePair.plateType, singlePlatePair.tangiblePlateTypesForComboPlateType)}
        />
      ) : null}

      <PlateDimensionsSelect platePairs={platePairs} />
      <PlateAngleInput platePairs={platePairs} />

      <PlateThicknessProperty platePairs={platePairs} />

      {singlePlatePair && hasPricingPermission && (
        <MaterialPropertyValuePair property="Plate Price (for the pair)" value={platePairPrice} />
      )}

      {singlePlatePair && <SinglePlatePairDesignValueProperties />}
    </PropertiesPanel>
  );
}

function SinglePlatePairDesignValueProperties() {
  const maxJsi = useAppSelector(getMaxJsiForSingleSelectedItem);

  return (
    <>
      {maxJsi != null ? <MaterialPropertyValuePair property="Max Jsi" value={maxJsi} /> : null}

      <Divider sx={{ marginTop: 2 }} />

      <Accordion sx={{ marginTop: 2 /* MUI default is to only have margin when open, which is strange */ }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>Design Values</AccordionSummary>

        <AccordionDetails>
          <DesignValueFields />
        </AccordionDetails>
      </Accordion>
    </>
  );
}
